<script lang="ts">
	//Svelte
	import { createEventDispatcher, onMount } from 'svelte';
	//Services
	import { format } from '../../../services/i18n';
	//Stores
	import { edit } from 'src/stores/ui/edit_lists';
	import { rooms } from 'src/stores/lists/rooms';
	//Design System components
	import { Modal, Button, Text, Input, Container } from '@Shelly/shelly-design-system';
	//Components
	import ImageList from '../../Lists/ImageList.svelte';
	//Helpers
	import { getImages, getImagesByType, requestSaveRoom } from '../../Lists/list-helpers';
	import { onItemSaved, orderItemsInRoom } from 'src/helpers/add-entity-helpers';
	//Type
	import type { Room } from 'src/types/Room';

	type RoomExtendedType = Partial<Room> & {
		overview_style?: boolean;
	};
	export let editRoom: RoomExtendedType = {
		name: undefined,
		image: '',
	};
	export let showModal: boolean;

	const dispatch = createEventDispatcher();
	const requestType = 'room';
	const ALL_ROOMS_ROUTE = '/home/rooms/-1';

	let roomNameValidationMsg = '';
	let uploadedImagesDB = [];
	let imagesByRoomType = [];
	let items: RoomExtendedType[] = Object.values($rooms);

	onMount(() => {
		imagesByRoomType = getImagesByType(requestType, null);
	});

	const clearData = () => {
		editRoom = {
			name: undefined,
			image: '',
		};
		roomNameValidationMsg = '';
	};

	const onCloseAddEditModal = () => {
		showModal = false;
		clearData();
		dispatch('close');
	};

	const onInputName = (event: InputEvent) => {
		const target = event.target as HTMLInputElement;
		editRoom = {
			...editRoom,
			name: target.value,
		};
	};

	const validateRoomName = (room: RoomExtendedType) => {
		const isEmpty = room.name === '';
		return {
			isValid: !isEmpty,
		};
	};

	const setRoomsEditChanges = (currentEditRoom: RoomExtendedType) => {
		if ($edit) {
			items = items.map((item) => {
				if (item.id === currentEditRoom.id) {
					return currentEditRoom;
				}
				return item;
			});
		} else {
			items = [...items, currentEditRoom];
		}
	};

	const handleAddEditRoomFinish = async () => {
		editRoom.name = editRoom.name.trim();
		let currentEditRoom = {
			...editRoom,
		};

		if (!$edit) {
			// new Room data
			await orderItemsInRoom(currentEditRoom, 'rooms');

			currentEditRoom = {
				...editRoom,
				main_sensor: false,
				overview_style: false,
				position: 0, // New items should be added at the first position to eliminate the need to scroll when adding a new item
			};
		}

		if (!validateRoomName(currentEditRoom).isValid) {
			return;
		}

		await requestSaveRoom(currentEditRoom, $edit);

		if (!$edit) {
			//Redirect to all rooms after create new
			onItemSaved(currentEditRoom, 'rooms');
		}

		onCloseAddEditModal();
	};

	// Image List Functions
	const onSelectImage = (e: CustomEvent) => {
		editRoom = {
			...editRoom,
			image: e.detail.image,
			backgroundColor: e.detail.backgroundColor,
		};
	};

	const onUploadImage = (e: CustomEvent) => {
		onSelectImage(e);
		uploadedImagesDB = [...e.detail.uploadedImagesDB];
	};

	const fetchUploadedImadesDB = async () => {
		uploadedImagesDB = await getImages(requestType);
	};

	$: disabled = editRoom?.name?.trim() === '' || typeof editRoom.name === 'undefined';

	$: items = Object.values($rooms);

	$: if (showModal) {
		fetchUploadedImadesDB();
	}
</script>

<Modal
	on:close={onCloseAddEditModal}
	{showModal}
	title={$format(`process_add_by_type`, {
		values: { type: $format('process_room') },
	})}
	removeScrollPadding
>
	<div slot="content">
		<Container padding="scrollPaddings" noScroll>
			<Text element="h2" class="mb-s">
				{$format('room_name', { default: 'Room Name' })}
			</Text>
			<Input
				id="input-room-name"
				aria-label="Input for Room Name"
				icon="fas fa-door-open"
				label={$format('label_name', { default: 'name' })}
				value={editRoom.name}
				error={editRoom.name === ''
					? $format('input_error_empty_name', {
							default: 'Name cannot be empty',
						})
					: ''}
				on:input={onInputName}
				on:enter={() => dispatch('enter')}
			/>
			<hr class="mt-l mb-l" />
		</Container>
		<Container padding="scrollPaddings" noScroll>
			<Text element="h2" class="mb-m">
				{$format('select_image', { default: 'Select image' })}
			</Text>

			<ImageList
				{uploadedImagesDB}
				imagesByTypeDB={imagesByRoomType}
				selectedImage={editRoom.image}
				selectedBackgroundColor={editRoom.backgroundColor}
				setDefaultImage={!$edit && showModal}
				{requestType}
				on:select-image={onSelectImage}
				on:upload-image={onUploadImage}
			/>
		</Container>
	</div>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={onCloseAddEditModal}
			aria-label={$format('aria_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={handleAddEditRoomFinish}
			{disabled}
		>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
</Modal>
